import { eq, randomString, toMilliseconds } from '@art-suite/art-core-ts';
import { useRef } from 'react';

const safeDisplayValue = <T>(
  value: T,
  displayValue?: (value: T) => string | undefined,
): string => {
  try {
    return !value
      ? `${value}`
      : typeof displayValue === 'function'
      ? displayValue(value) ?? value.toString()
      : value.toString();
  } catch (error) {
    return '(error displaying value)';
  }
};

const startTime = toMilliseconds();

export const useDebugChanged = <T>(
  name: string,
  value: T,
  displayValue?: (value: T) => string | undefined,
): void => {
  const lastValue = useRef(value);
  if (lastValue.current !== value) {
    const from = safeDisplayValue(lastValue.current, displayValue);
    let to = safeDisplayValue(value, displayValue);
    const deepEq = eq(lastValue.current, value);
    const tripleEq = lastValue.current === value;
    const displayEquals = from === to;
    if (displayEquals) to = '(same)';
    if (from.length > 50 || to.length > 50) {
      console.log(
        `useDebugChanged (${
          toMilliseconds() - startTime
        }ms) ${name}: (eq: ${deepEq}, ===: ${tripleEq}, string-equals: ${displayEquals}).\n  From: ${from}\n  To: ${to}`,
      );
    } else {
      console.log(
        `useDebugChanged (${
          toMilliseconds() - startTime
        }ms) ${name}: (eq: ${deepEq}, ===: ${tripleEq}, string-equals: ${displayEquals}). From: ${from} To: ${to}`,
      );
    }
    lastValue.current = value;
  }
};

export const useDebugChanges = (
  context: string,
  values: Record<string, any>,
): void => {
  const uniqueId = useRef(randomString(4));
  const lastValuesRef = useRef(values);
  const lastValues = lastValuesRef.current;
  for (const key in values) {
    if (lastValues[key] !== values[key]) {
      console.log(`useDebugChanges (${context}-${uniqueId.current}): ${key}`, { before: lastValues[key], after: values[key] });
    }
  }
  lastValuesRef.current = values;
};
